import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { FINANCE_QA_EVENT, messageLengthBucket, capture, financeQaProps } from '../../analytics/posthog';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import config, { logger } from '../../config';
import { handleApiResponseError, getToken, getOnboardingAIData, getApiHeaders, apiFetch, updateV2OnboardingAiResult } from '../../utils/api';
import { saveOnboardingAIChatToStorage, parseTextWithBullets, AI_RESPONSE_INSTRUCTIONS, getUserInitials } from '../../utils/helper';
import { useContentCatalog } from '../../utils/contentCatalog';
import FeatureInProgressModal from '../shared/FeatureInProgressModal';
import FinancialProfilePanel, {
  FINANCIAL_PROFILE_DEFAULTS,
  getV2OnboardingAiResultFromResponse,
  pickFinancialProfileFromV2Result,
} from '../OnboardingAgent/FinancialProfilePanel';
import { ProfileDrawer } from '../OnboardingAgent/V2OnboardingAgent/ChatUI';
import { useProfileDrawer } from '../OnboardingAgent/V2OnboardingAgent/useProfileDrawer';
import { MOBILE_MQ } from '../OnboardingAgent/V2OnboardingAgent/helpers';
import v2ShellStyles from '../OnboardingAgent/V2OnboardingAgent.module.css';
import styles from './TalkToReesePage.module.css';

function mergePreferNonBlank(base, overlay) {
  const out = { ...(base || {}) };
  for (const [k, v] of Object.entries(overlay || {})) {
    const existing = out[k];
    const hasExisting = existing !== undefined && existing !== null && String(existing).trim() !== '';
    if (!hasExisting) out[k] = v;
  }
  return out;
}

/** Same cap as V2 onboarding composer */
const INPUT_MAX_LEN = 2000;

function joinPrefixWithTranscript(prefix, transcriptChunk) {
  const p = (prefix ?? '').trimEnd();
  const t = (transcriptChunk ?? '').trim();
  if (!p) return t.slice(0, INPUT_MAX_LEN);
  if (!t) return p.slice(0, INPUT_MAX_LEN);
  return `${p} ${t}`.slice(0, INPUT_MAX_LEN);
}

const MAX_CHAT_QUESTIONS = 5;
const SCROLL_DELAY_MS = 100;
const ERROR_MESSAGE_GENERIC = "I apologize, but I'm having trouble processing your request. Please try again.";
const ERROR_MESSAGE_TECHNICAL = "I apologize, but I'm experiencing technical difficulties. Please try again in a moment.";
const QUESTION_LIMIT_MESSAGE_DEFAULT = `You have reached the maximum limit of ${MAX_CHAT_QUESTIONS} questions.`;
const INPUT_PLACEHOLDER = 'Type your answer…';

const normalizeTimestamp = (value) => {
  if (!value) {
    return new Date();
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const mapChatHistoryToMessages = (chatHistory) => {
  if (!Array.isArray(chatHistory)) {
    return { messages: [], suggestedResponsesMap: {} };
  }

  const messages = [];
  const suggestedResponsesMap = {};
  let lastAiMessageId = null;
  let latestSuggestedResponses = null;

  // First pass: collect all messages and find the latest suggested responses
  chatHistory.forEach((chatItem, index) => {
    const rawType = chatItem?.type || chatItem?.role;

    // Handle suggested responses - they come as an array in chat field
    if (rawType === 'suggested') {
      const suggestedArray = chatItem?.chat;
      // Check if chat is an array (new format) or string (old format for backward compatibility)
      if (Array.isArray(suggestedArray) && suggestedArray.length > 0) {
        // Always keep the latest suggested responses (overwrite previous ones)
        latestSuggestedResponses = suggestedArray;
      } else if (typeof suggestedArray === 'string') {
        // Backward compatibility: if it's a string, treat as old format
        if (!latestSuggestedResponses) {
          latestSuggestedResponses = [];
        }
        if (!Array.isArray(latestSuggestedResponses)) {
          latestSuggestedResponses = [latestSuggestedResponses];
        }
        latestSuggestedResponses.push(suggestedArray);
      }
      return; // Skip suggested items - they won't be added to messages
    }

    const text = chatItem?.chat ?? chatItem?.text ?? chatItem?.message ?? chatItem?.content;
    if (!text) {
      return;
    }

    const normalizedType = (() => {
      if (rawType === 'question' || rawType === 'user' || rawType === 'human') {
        return 'user';
      }
      return 'ai';
    })();

    const messageId = chatItem?.id || `history-${index}`;
    const message = {
      id: messageId,
      type: normalizedType,
      text,
      timestamp: normalizeTimestamp(chatItem?.timestamp || chatItem?.created_at || chatItem?.createdAt),
    };

    messages.push(message);

    // Track the last AI message ID to associate suggested responses
    if (normalizedType === 'ai') {
      lastAiMessageId = messageId;
    }
  });

  // Only associate the latest suggested responses with the last AI message
  if (lastAiMessageId && latestSuggestedResponses && Array.isArray(latestSuggestedResponses) && latestSuggestedResponses.length > 0) {
    suggestedResponsesMap[lastAiMessageId] = [...latestSuggestedResponses];
  }

  return { messages, suggestedResponsesMap };
};

const messagesToChatRecords = (messageList, suggestedResponses = null, lastAiMessageId = null) => {
  if (!Array.isArray(messageList)) {
    return [];
  }

  const records = messageList
    .filter((message) => message && (message.type === 'user' || message.type === 'ai'))
    .map((message) => ({
      type: message.type === 'user' ? 'question' : 'answer',
      chat: message.text,
      timestamp: normalizeTimestamp(message.timestamp).getTime()
    }))
    .filter((record) => typeof record.chat === 'string' && record.chat.trim() !== '');

  // Add suggested responses as a single record with type "suggested" (array format, no timestamp)
  if (suggestedResponses && Array.isArray(suggestedResponses) && suggestedResponses.length > 0 && lastAiMessageId) {
    const lastAiMessage = messageList.find(m => m.id === lastAiMessageId);
    if (lastAiMessage) {
      records.push({
        type: 'suggested',
        chat: suggestedResponses
      });
    }
  }

  return records;
};

const formatTime = (date) => {
  const d = normalizeTimestamp(date);
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const h = hours % 12 || 12;
  const m = minutes.toString().padStart(2, '0');
  return `${h}:${m}${ampm}`;
};

const RESPONSE_GUIDELINES = `
- Response format: Reply with plain text only (no JSON, no code blocks).
- Response length: Keep your answer to a maximum of 4 sentences while retaining all key information. Be direct and to the point while still being helpful and complete.
- Formatting: You may use bullet points (•, -, or *) when appropriate. You can also use **bold** markdown to emphasize important information.
- Tone: Be warm, empathetic, and genuinely supportive.`;

const PROMPT_WITH_CONTEXT_PREFIX = `You are Reese, a caring and empathetic AI financial guide helping someone navigate a career transition. Below is important background information about the user that you've learned through their onboarding process. Use this context to understand their situation deeply and provide personalized, supportive guidance.

`;
const PROMPT_WITH_CONTEXT_SUFFIX = `

The user is now asking you this question:
"
`;
const PROMPT_WITHOUT_CONTEXT_PREFIX = `You are Reese, a caring and empathetic AI financial guide helping someone navigate a career transition.

The user is asking: "
`;
const PROMPT_TAIL = `

IMPORTANT: Reply with plain text only (no JSON, no code blocks).`;

function buildReesePrompt(hasUserContext, userContext, userQuestion) {
  const quoted = userQuestion.trim();
  if (hasUserContext && userContext) {
    return `${PROMPT_WITH_CONTEXT_PREFIX}${userContext}${PROMPT_WITH_CONTEXT_SUFFIX}${quoted}"
${AI_RESPONSE_INSTRUCTIONS}
${RESPONSE_GUIDELINES}
- Personalization: Acknowledge their unique situation using their background.
- Understanding: Show understanding of career transition, financial concerns, family situation.
- Encouragement: Be encouraging and help them feel confident.
- Practicality: Offer practical, actionable guidance when appropriate.
- Compassion: Remember they're going through a significant life transition.
${PROMPT_TAIL}`;
  }
  return `${PROMPT_WITHOUT_CONTEXT_PREFIX}${quoted}"
${AI_RESPONSE_INSTRUCTIONS}
${RESPONSE_GUIDELINES}
- Engagement: Respond in a conversational, open-ended manner that encourages dialogue.
- Practicality: Offer practical, actionable guidance when appropriate.
${PROMPT_TAIL}`;
}

/** Finance Q&A header — same column role as V2 `v2-header` (flex-shrink: 0; stays above scroll region). */
function ChatHeader() {
  return (
    <header className={styles.chatHeaderModern}>
      <div className={styles.chatHeaderTop}>
        <div className={styles.chatHeaderTopRow}>
          <div className={styles.qaBadge} aria-hidden>
            <span>QA</span>
          </div>
          <div className={styles.chatHeaderTextBlock}>
            <h1 className={styles.chatTitleModern}>Finance Q&amp;A</h1>
            <p className={styles.chatSubtitle}>Powered by Reese · General finance questions only</p>
          </div>
        </div>
        <p className={styles.chatLead}>
          Ask me anything about personal finance, investing, taxes, or retirement — I won&apos;t touch your profile data.
        </p>
      </div>
    </header>
  );
}

function MessageBubbleContent({ type, text }) {
  if (type === 'user') {
    return <p className={styles.messageText}>{text}</p>;
  }
  const lines = parseTextWithBullets(text);
  return (
    <div className={styles.messageText}>
      {lines.map((line, lineIndex) => {
        if (line.isBullet) {
          return (
            <div key={lineIndex} className={styles.bulletPoint}>
              <span className={styles.bullet}>•</span>
              <span>
                {line.parts.map((part, partIndex) =>
                  part.type === 'bold' ? (
                    <strong key={partIndex} className={styles.boldText}>{part.content}</strong>
                  ) : (
                    <React.Fragment key={partIndex}>{part.content}</React.Fragment>
                  )
                )}
              </span>
            </div>
          );
        }
        return (
          <p key={lineIndex}>
            {line.parts.map((part, partIndex) =>
              part.type === 'bold' ? (
                <strong key={partIndex} className={styles.boldText}>{part.content}</strong>
              ) : (
                <React.Fragment key={partIndex}>{part.content}</React.Fragment>
              )
            )}
          </p>
        );
      })}
    </div>
  );
}

const TalkToReesePage = ({ embedded = false, topBar = null, showScenarioCta = false } = {}) => {
  const { catalog } = useContentCatalog();
  const suggestedResponses = catalog?.reese_prompts || [];
  const navigate = useNavigate();
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;

  const [userAnswers, setUserAnswers] = useState({});
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedHistory, setHasFetchedHistory] = useState(false);
  const [questionLimitModalMessage, setQuestionLimitModalMessage] = useState('');
  const [showPricingView] = useState(false);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef(messages);

  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const speechPrefixRef = useRef('');
  const transcriptRef = useRef('');
  const speechCommitInFlightRef = useRef(false);
  transcriptRef.current = transcript;

  const updateMessages = useCallback((updater) => {
    const prevMessages = messagesRef.current;
    const nextMessages = typeof updater === 'function' ? updater(prevMessages) : updater;
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
  }, []);

  const commitSpeechToInput = useCallback(async () => {
    if (!listening || speechCommitInFlightRef.current) return;
    speechCommitInFlightRef.current = true;
    try {
      const next = joinPrefixWithTranscript(speechPrefixRef.current, transcriptRef.current);
      await SpeechRecognition.stopListening();
      resetTranscript();
      setInputMessage(next);
    } finally {
      speechCommitInFlightRef.current = false;
    }
  }, [listening, resetTranscript]);

  useEffect(() => () => {
    SpeechRecognition.stopListening();
  }, []);

  const persistChatExchange = useCallback(async (tokenValue, questionMessage, answerMessage) => {
    if (!tokenValue || !questionMessage?.text || !answerMessage?.text) {
      return;
    }

    const payload = messagesToChatRecords(messagesRef.current);

    if (payload.length === 0) {
      return;
    }

    try {
      const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_UPDATE_CHAT}`, {
        method: 'PUT',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          token: tokenValue,
          chat: payload
        })
      });

      if (response.status === 400) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          logger.error('Error parsing chat update error response:', jsonError);
        }

        if (errorData?.error_code === 'QUESTION_LIMIT_REACHED') {
          capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'question_limit_reached', source: 'persist_chat' }));
          setQuestionLimitModalMessage(errorData?.message ?? QUESTION_LIMIT_MESSAGE_DEFAULT);
          return;
        }

        localStorage.clear();
        navigate('/login');
        return;
      }

      if (handleApiResponseError(response, navigate, false)) {
        return;
      }

      if (!response.ok) {
        logger.error(`Unexpected response while updating chat. Status: ${response.status}`);
        localStorage.clear();
        navigate('/login');
      }
    } catch (error) {
      logger.error('Error updating chat history:', error);
      localStorage.clear();
      navigate('/login');
    }
  }, [navigate, embedded]);

  const handleStartTrial = useCallback(() => {
    capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'start_trial' }));
    setIsFeatureModalOpen(true);
  }, [posthog, embedded]);

  const handleCloseFeatureModal = useCallback(() => {
    setIsFeatureModalOpen(false);
  }, []);

  const shownReplyId = hasFetchedHistory && !isLoading
    ? [...messages].reverse().find((message) => message?.type === 'ai')?.id ?? null
    : null;

  const profileDrawer = useProfileDrawer({
    autoReveal: embedded,
    pinnedClosed: !embedded,
    initialOpen: embedded && typeof window !== 'undefined' && !window.matchMedia(MOBILE_MQ).matches,
    delayInitialOpen: embedded,
    messagesVisible: profileReady && hasFetchedHistory && !isLoading,
    responseId: shownReplyId,
    active: profileReady && hasFetchedHistory,
    onToggle: (next) => {
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, {
        action: 'mobile_profile_drawer',
        open: next,
        viewport: window.matchMedia(MOBILE_MQ).matches ? 'mobile' : 'desktop',
      }));
    },
    onAutoReveal: (trigger, viewport) => {
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, {
        action: 'profile_drawer_auto_reveal',
        trigger,
        viewport,
      }));
    },
  });
  const mobileProfileOpen = profileDrawer.open;

  const persistV2OnboardingAiResult = useCallback(async (token, financialProfile) => {
    if (!token) return false;
    const result = await updateV2OnboardingAiResult(
      {
        token,
        v2OnboardingAiResult: {
          financial_profile: financialProfile,
          updated_at: Date.now(),
        },
      },
      navigate
    );
    if (!result.ok) {
      logger.warn('Failed to seed V2 onboarding result:', result.message || 'Unknown error');
      return false;
    }
    return true;
  }, [navigate]);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;

    const run = async () => {
      try {
        const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN);
        if (!token) {
          return;
        }

        const data = await getOnboardingAIData(token, navigate, false, signal);
        if (signal.aborted || data == null) {
          return;
        }

        const apiV2Result = getV2OnboardingAiResultFromResponse(data);
        const profileFromApi = pickFinancialProfileFromV2Result(apiV2Result);
        const shouldSeedDefaults = apiV2Result == null || profileFromApi == null;
        const seedProfile = shouldSeedDefaults ? { ...FINANCIAL_PROFILE_DEFAULTS } : profileFromApi;

        if (shouldSeedDefaults) {
          await persistV2OnboardingAiResult(token, seedProfile);
        }
        if (signal.aborted) {
          return;
        }

        setUserAnswers((prev) =>
          shouldSeedDefaults ? mergePreferNonBlank(prev, seedProfile) : { ...prev, ...seedProfile }
        );
      } catch (e) {
        if (e?.name === 'AbortError' || signal.aborted) {
          return;
        }
        logger.error('TalkToReese V2 profile load error:', e);
      } finally {
        if (!signal.aborted) setProfileReady(true);
      }
    };

    run();
    return () => ac.abort();
  }, [navigate, persistV2OnboardingAiResult]);

  const handlePlanSelect = useCallback(
    (planName) => {
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'plan_select', plan: planName }));
      setSelectedPlan(planName);
    },
    [posthog, embedded]
  );

  const handleBillingToggle = useCallback(() => {
    capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'billing_toggle' }));
    setIsAnnual((prev) => !prev);
  }, [posthog, embedded]);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;

    const fetchChatHistory = async () => {
      const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN);
      if (!token) {
        if (signal.aborted) return;
        localStorage.clear();
        navigate('/login');
        setHasFetchedHistory(true);
        return;
      }

      try {
        const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.USER_GET_CHAT}`, {
          method: 'GET',
          headers: getApiHeaders({ 'Content-Type': 'application/json' }, token),
          signal,
        });

        if (signal.aborted) return;

        if (handleApiResponseError(response, navigate, false)) {
          return;
        }

        if (response.status === 400) {
          let errorData = null;
          try {
            errorData = await response.json();
          } catch (jsonError) {
            logger.error('Error parsing chat history error response:', jsonError);
          }

          if (signal.aborted) return;

          if (errorData?.error_code === 'QUESTION_LIMIT_REACHED') {
            capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'question_limit_reached', source: 'fetch_history' }));
            const historyFromError = errorData?.data?.chat;
            const { messages: formattedHistoryFromError } = mapChatHistoryToMessages(historyFromError);

            if (formattedHistoryFromError.length > 0) {
              updateMessages(formattedHistoryFromError);
            }

            setQuestionLimitModalMessage(errorData?.message ?? QUESTION_LIMIT_MESSAGE_DEFAULT);
            return;
          }

          logger.error(`Failed to fetch chat history. Status: ${response.status}`);
          return;
        }

        if (!response.ok) {
          logger.error(`Failed to fetch chat history. Status: ${response.status}`);
          return;
        }

        const data = await response.json();
        if (signal.aborted) return;

        const chatHistory = data?.data?.chat;

        const { messages: formattedHistory } = mapChatHistoryToMessages(chatHistory);

        if (formattedHistory.length > 0) {
          updateMessages(formattedHistory);
        }
        capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, {
          action: 'chat_history_loaded',
          message_count: formattedHistory.length,
        }));
      } catch (error) {
        if (error?.name === 'AbortError' || signal.aborted) return;
        logger.error('Error fetching chat history:', error);
        capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'chat_history_load_failed' }));
      } finally {
        if (!signal.aborted) {
          setHasFetchedHistory(true);
        }
      }
    };

    fetchChatHistory();
    return () => ac.abort();
  }, [navigate, updateMessages, embedded]);

  const scrollToBottom = useCallback(() => {
    const mobileDocScroll =
      typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;
    const behavior = mobileDocScroll ? 'auto' : 'smooth';
    const delay = mobileDocScroll ? 0 : SCROLL_DELAY_MS;
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end', inline: 'nearest' });
    }, delay);
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    scrollToBottom();
  }, [messages, scrollToBottom]);

  /**
   * Builds a context string from user's onboarding data stored in localStorage
   * This provides background information about the user to help AI give personalized responses
   * Only works for onboarding-agent users
   *
   * @returns {Promise<string>} Formatted context string with user's onboarding Q&A pairs
   */
  const buildUserContext = useCallback(async () => {
    try {
      let onboardingAIDataStr = localStorage.getItem(config.STORAGE_KEYS.ONBOARDING_AI_CHAT);

      // If no data in localStorage, fetch from API
      if (!onboardingAIDataStr) {
        const token = getToken();
        if (!token) {
          logger.warn('No token found, cannot fetch onboarding AI data');
          return '';
        }

        try {
          const apiData = await getOnboardingAIData(token, navigate, false);

          if (apiData && apiData.success && apiData.data) {
            // Save filtered chat data to localStorage using helper function
            saveOnboardingAIChatToStorage(apiData, config);

            // Get the saved data from localStorage
            onboardingAIDataStr = localStorage.getItem(config.STORAGE_KEYS.ONBOARDING_AI_CHAT);

            if (!onboardingAIDataStr) {
              return '';
            }
          } else {
            return '';
          }
        } catch (error) {
          logger.error('Error fetching onboarding AI data from API:', error);
          return '';
        }
      }

      const onboardingAIData = JSON.parse(onboardingAIDataStr);
      if (!Array.isArray(onboardingAIData) || onboardingAIData.length === 0) {
        return '';
      }

      // Group questions and answers by their ID
      const qaPairs = {};
      onboardingAIData.forEach((message) => {
        if (message.type === 'question' && message.id) {
          if (!qaPairs[message.id]) {
            qaPairs[message.id] = { question: message.text, answer: null };
          }
        } else if (message.type === 'answer' && message.id) {
          if (!qaPairs[message.id]) {
            qaPairs[message.id] = { question: null, answer: message.text };
          } else {
            qaPairs[message.id].answer = message.text;
          }
        }
      });

      // Build context string from Q&A pairs
      const contextParts = [];
      contextParts.push('USER BACKGROUND INFORMATION (from onboarding):');
      contextParts.push('');

      Object.entries(qaPairs).forEach(([id, pair]) => {
        if (pair.question && pair.answer) {
          contextParts.push(`Q: ${pair.question}`);
          contextParts.push(`A: ${pair.answer}`);
          contextParts.push('');
        }
      });

      if (contextParts.length <= 2) {
        // Only header was added, no actual Q&A pairs
        return '';
      }

      return contextParts.join('\n');
    } catch (error) {
      logger.error('Error building user context from onboarding data:', error);
      return '';
    }
  }, [navigate]);

  const sendMessage = useCallback(
    async (messageText) => {
      if (!messageText.trim() || isLoading) return;

      const userMessage = {
        id: `user-${Date.now()}`,
        type: 'user',
        text: messageText.trim(),
        timestamp: new Date()
      };

      updateMessages((prev) => [...prev, userMessage]);
      setInputMessage('');
      speechPrefixRef.current = '';
      setIsLoading(true);

      try {
        const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN);
        if (!token) {
          localStorage.clear();
          navigate('/login');
          return;
        }

        const userContext = await buildUserContext();
        const enhancedQuestion = buildReesePrompt(Boolean(userContext), userContext ?? '', messageText.trim());

        const response = await apiFetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.ONBOARDING_AI_CHAT}`, {
          method: 'POST',
          headers: getApiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ token, question: enhancedQuestion })
        });

        if (handleApiResponseError(response, navigate, false)) {
          capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'ai_request_failed', reason: 'session_or_auth' }));
          updateMessages((prev) => [...prev, { id: `ai-error-${Date.now()}`, type: 'ai', text: ERROR_MESSAGE_GENERIC, timestamp: new Date() }]);
          setIsLoading(false);
          return;
        }

        if (!response.ok) {
          logger.error(`API request failed with status ${response.status}`);
          capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, {
            action: 'ai_request_failed',
            reason: 'http_error',
            status: response.status,
          }));
          updateMessages((prev) => [...prev, { id: `ai-error-${Date.now()}`, type: 'ai', text: ERROR_MESSAGE_GENERIC, timestamp: new Date() }]);
          setIsLoading(false);
          return;
        }

        const data = await response.json();
        const rawResponse = data.data?.answer || data.response || data.data?.response || "I'm sorry, I couldn't process that. Could you please rephrase your question?";
        const aiMessage = {
          id: `ai-${Date.now()}`,
          type: 'ai',
          text: rawResponse,
          timestamp: new Date()
        };

        updateMessages((prev) => [...prev, aiMessage]);
        await persistChatExchange(token, userMessage, aiMessage);
        capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, {
          action: 'ai_response_received',
          length_bucket: messageLengthBucket(messageText.trim().length),
          has_onboarding_context: Boolean(String(userContext || '').trim()),
        }));
      } catch (error) {
        logger.error('Error sending message:', error);
        capture(posthogRef.current, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'ai_request_failed', reason: 'exception' }));
        updateMessages((prev) => [...prev, { id: `ai-error-${Date.now()}`, type: 'ai', text: ERROR_MESSAGE_TECHNICAL, timestamp: new Date() }]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, updateMessages, navigate, buildUserContext, persistChatExchange, embedded]
  );

  const handleSuggestedResponse = useCallback(
    (suggestion) => {
      if (!suggestion?.question) return;
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'suggested_response', title: suggestion?.title ?? '' }));
      setInputMessage(suggestion.question);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [posthog, embedded]
  );

  const composerDisplayValue = listening
    ? joinPrefixWithTranscript(speechPrefixRef.current, transcript)
    : inputMessage;

  const composerSubmitValue = listening
    ? joinPrefixWithTranscript(speechPrefixRef.current, transcriptRef.current)
    : inputMessage;
  const canSendComposer = Boolean(composerSubmitValue.trim()) && !isLoading;

  // Ensure textarea resizes when content changes programmatically (e.g. suggested responses)
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    try {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
      // put caret at end so user sees the full text when focusing
      if (!listening && typeof el.setSelectionRange === 'function') {
        const len = el.value?.length || 0;
        el.setSelectionRange(len, len);
      }
    } catch (e) {}
  }, [composerDisplayValue, listening]);

  const handleMicClick = useCallback(async () => {
    if (!browserSupportsSpeechRecognition || isLoading) return;
    if (listening) {
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'voice_commit' }));
      await commitSpeechToInput();
      return;
    }
    try {
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'voice_start' }));
      speechPrefixRef.current = inputMessage;
      resetTranscript();
      await SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
    } catch (err) {
      logger.error('Speech recognition start failed:', err);
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'voice_start_failed' }));
    }
  }, [
    browserSupportsSpeechRecognition,
    isLoading,
    listening,
    commitSpeechToInput,
    inputMessage,
    resetTranscript,
    posthog,
    embedded,
  ]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isLoading) return;
      let textForSubmit = inputMessage;
      if (listening) {
        textForSubmit = joinPrefixWithTranscript(speechPrefixRef.current, transcriptRef.current);
        await SpeechRecognition.stopListening();
        resetTranscript();
        setInputMessage(textForSubmit);
      }
      const text = textForSubmit.replace(/\s+$/, '').trim();
      if (!text) return;
      capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'send_message', length_bucket: messageLengthBucket(text.length) }));
      speechPrefixRef.current = '';
      sendMessage(text);
    },
    [inputMessage, isLoading, listening, resetTranscript, sendMessage, posthog, embedded]
  );

  const showSuggestedResponses = hasFetchedHistory && suggestedResponses.length > 0;
  const lastAiMessageId = [...messages].reverse().find((m) => m?.type === 'ai')?.id ?? null;
  const suggestedSection = (
    <section className={styles.suggestedSection}>
      <h2 className={styles.suggestedHeading}>TRY ASKING</h2>
      <div className={styles.suggestedResponsesModern}>
        {suggestedResponses.map((suggestion) => (
          <button
            key={suggestion.title}
            className={styles.suggestedButtonModern}
            onClick={() => handleSuggestedResponse(suggestion)}
            disabled={isLoading}
          >
            {suggestion.title}
          </button>
        ))}
      </div>
    </section>
  );

  const pageClassName = `${styles.talkToReesePage}${embedded ? ` ${styles.talkToReesePageEmbedded}` : ''}`;

  if (showPricingView) {
    return (
      <div className={pageClassName}>
        <FeatureInProgressModal isOpen={isFeatureModalOpen} onClose={handleCloseFeatureModal} />
        <div className={styles.pricingContainer}>
          <div className={styles.pricingHeader}>
            <h1 className={styles.pricingTitle}>Choose the plan that's right for you</h1>
            <p className={styles.pricingSubtitle}>
              Simple, transparent pricing. No hidden fees. All plans come with a 14-day free trial.
            </p>

            {/* Billing Toggle */}
            <div className={styles.billingToggle}>
              <span className={`${styles.toggleLabel} ${!isAnnual ? styles.active : ''}`}>Monthly</span>
              <button
                type="button"
                className={`${styles.toggleSwitch} ${isAnnual ? styles.toggleActive : ''}`}
                onClick={handleBillingToggle}
                aria-label="Toggle billing period"
              >
                <span className={styles.toggleHandle}></span>
              </button>
              <span className={styles.toggleLabel}>
                Annual <span className={styles.saveBadge}>(Save 20%)</span>
              </span>
            </div>
          </div>

          <div className={styles.pricingGrid}>
            {/* Plus Plan */}
            <div
              className={`${styles.pricingCard} ${selectedPlan === 'plus' ? styles.selectedCard : ''}`}
              onClick={() => handlePlanSelect('plus')}
            >
              <div className={styles.planHeader}>
                <h2 className={styles.planName}>Plus</h2>
                <div className={styles.planPrice}>
                  <span className={styles.priceAmount}>{isAnnual ? '$24' : '$29'}</span>
                  <span className={styles.pricePeriod}>/month</span>
                </div>
              </div>
              <ul className={styles.planFeatures}>
                <li>Basic financial planning</li>
                <li>Monthly budget tracking</li>
                <li>Email support</li>
                <li>Access to basic tools</li>
              </ul>
              <button className={styles.trialButton} onClick={handleStartTrial}>
                Start 14-days Trial
              </button>
            </div>

            {/* Pro Plan */}
            <div
              className={`${styles.pricingCard} ${styles.featuredCard} ${selectedPlan === 'pro' ? styles.selectedCard : ''} ${!selectedPlan ? styles.featuredDefault : ''}`}
              onClick={() => handlePlanSelect('pro')}
            >
              <div className={styles.badge}>Most Popular</div>
              <div className={styles.planHeader}>
                <h2 className={styles.planName}>Pro</h2>
                <div className={styles.planPrice}>
                  <span className={styles.priceAmount}>{isAnnual ? '$63' : '$79'}</span>
                  <span className={styles.pricePeriod}>/month</span>
                </div>
              </div>
              <ul className={styles.planFeatures}>
                <li>Advanced financial planning</li>
                <li>Real-time budget tracking</li>
                <li>Priority support</li>
                <li>Access to all tools</li>
                <li>Investment recommendations</li>
              </ul>
              <button className={`${styles.trialButton} ${styles.featuredButton}`} onClick={handleStartTrial}>
                Start 14-days Trial
              </button>
            </div>

            {/* Ultra Plan */}
            <div
              className={`${styles.pricingCard} ${selectedPlan === 'ultra' ? styles.selectedCard : ''}`}
              onClick={() => handlePlanSelect('ultra')}
            >
              <div className={styles.planHeader}>
                <h2 className={styles.planName}>Ultra</h2>
                <div className={styles.planPrice}>
                  <span className={styles.priceAmount}>{isAnnual ? '$119' : '$149'}</span>
                  <span className={styles.pricePeriod}>/month</span>
                </div>
              </div>
              <ul className={styles.planFeatures}>
                <li>Premium financial planning</li>
                <li>Advanced analytics</li>
                <li>24/7 dedicated support</li>
                <li>All premium tools</li>
                <li>Personal financial guide</li>
                <li>Custom investment strategies</li>
              </ul>
              <button className={styles.trialButton} onClick={handleStartTrial}>
                Start 14-days Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const splitShellClass = `${v2ShellStyles['v2-shell']} ${embedded ? v2ShellStyles['v2-shell--embedded'] : ''} ${embedded ? v2ShellStyles['v2-shell--profile-drawer'] : ''} ${embedded && mobileProfileOpen ? v2ShellStyles['v2-shell--mobile-profile-open'] : ''} ${styles.reeseSplitShell}`;

  /*
   * Chat column mirrors V2OnboardingAgent: `chat-container` → header (fixed height) → `v2-chat-body`
   * → `messages-container` (only vertical scroll + thin scrollbar) → `v2-input-area` (always visible).
   */
  return (
    <div className={pageClassName}>
      <FeatureInProgressModal isOpen={isFeatureModalOpen} onClose={handleCloseFeatureModal} />
      <div className={splitShellClass}>
        <div className={v2ShellStyles['v2-chat-column']}>
          {topBar}
          <div
            className={`${v2ShellStyles['chat-container']} ${v2ShellStyles['chat-container--v2']} ${styles.reeseChatShell}`}
          >
            <ChatHeader />

            <div className={`${v2ShellStyles['v2-chat-body']} ${styles.reeseChatBody}`}>
              <div className={`${v2ShellStyles['messages-container']} ${styles.reeseMessagesScroll}`}>
                <div className={`${v2ShellStyles['messages-inner']} ${styles.reeseMessagesInner}`}>
                  {messages.map((message) => (
                    <React.Fragment key={message.id}>
                      <div
                        className={`${styles.chatRow} ${message.type === 'user' ? styles.chatRowUser : styles.chatRowAi}`}
                      >
                        {message.type === 'ai' ? (
                          <span className={styles.agentBadge} aria-hidden>
                            <span>QA</span>
                          </span>
                        ) : null}
                        <div className={`${styles.chatBubble} ${message.type === 'user' ? styles.chatBubbleUser : styles.chatBubbleAi}`}>
                          <MessageBubbleContent type={message.type} text={message.text} />
                          <div className={styles.timestampModern}>{formatTime(message.timestamp)}</div>
                        </div>
                        {message.type === 'user' ? <span className={styles.userBadge}>{getUserInitials()}</span> : null}
                      </div>

                      {showSuggestedResponses && message.type === 'ai' && message.id === lastAiMessageId && suggestedSection}
                    </React.Fragment>
                  ))}

                  {showSuggestedResponses && !lastAiMessageId && suggestedSection}

                  {isLoading && (
                    <div className={styles.chatRow}>
                      <div className={`${styles.chatBubble} ${styles.chatBubbleAi}`}>
                        <div className={styles.typingIndicator}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className={v2ShellStyles['v2-input-area']}>
                <button
                  type="button"
                  className={`${v2ShellStyles['v2-mic-button']}${listening ? ` ${v2ShellStyles['v2-mic-button--active']}` : ''}`}
                  aria-label={listening ? 'Stop voice input' : 'Start voice input'}
                  aria-pressed={listening}
                  title={
                    !browserSupportsSpeechRecognition
                      ? 'Voice input is not supported in this browser'
                      : listening
                        ? 'Listening — click to stop and keep text'
                        : 'Click to speak — your words appear in the answer field'
                  }
                  disabled={!browserSupportsSpeechRecognition || isLoading}
                  onClick={handleMicClick}
                >
                  {listening ? (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                      <path d="M6 9h.01M10 9h.01M14 9h.01M18 9h.01M8 13h.01M12 13h.01M16 13h.01M7 17h10" />
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
                      <path d="M19 11a7 7 0 01-14 0M12 18v3M8 22h8" />
                    </svg>
                  )}
                </button>
                <form
                  className={`${v2ShellStyles['v2-input-form']} ${v2ShellStyles['v2-input-area-inner']}`}
                  onSubmit={handleSubmit}
                >
                  <textarea
                    ref={inputRef}
                    value={composerDisplayValue}
                    onChange={(ev) => {
                      if (listening) return;
                      setInputMessage(ev.target.value.slice(0, INPUT_MAX_LEN));
                    }}
                    onInput={(ev) => {
                      try {
                        ev.target.style.height = 'auto';
                        ev.target.style.height = `${ev.target.scrollHeight}px`;
                      } catch (e) {}
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' && !listening) {
                        ev.preventDefault();
                        ev.currentTarget.form?.requestSubmit();
                      }
                    }}
                    readOnly={listening}
                    placeholder={listening ? 'Listening… speak now' : INPUT_PLACEHOLDER}
                    className={`${v2ShellStyles['text-input']} ${v2ShellStyles['text-input--v2']} ${v2ShellStyles['textarea--v2'] || ''}`}
                    disabled={isLoading}
                    maxLength={INPUT_MAX_LEN}
                    aria-label="Your message"
                    enterKeyHint="send"
                    autoComplete="off"
                    rows={1}
                  />
                  <button
                    type="submit"
                    className={v2ShellStyles['v2-send-button']}
                    disabled={!canSendComposer}
                    aria-label="Send"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <ProfileDrawer
          open={mobileProfileOpen}
          showChrome={embedded}
          onClose={profileDrawer.close}
          onToggle={profileDrawer.toggle}
          onHandleTouchStart={profileDrawer.handleTouchStart}
          onHandleTouchEnd={profileDrawer.handleTouchEnd}
          onDrawerTouchStart={profileDrawer.drawerTouchStart}
          onDrawerTouchEnd={profileDrawer.drawerTouchEnd}
        >
          <FinancialProfilePanel
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            showScenarioCta={showScenarioCta}
          />
        </ProfileDrawer>
      </div>

      {questionLimitModalMessage && (
        <div className={styles.modalOverlay} role="presentation">
          <div
            className={styles.modalContent}
            role="dialog"
            aria-modal="true"
            aria-labelledby="question-limit-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalIcon} aria-hidden>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                <path d="M8 10h8M8 14h5" />
              </svg>
            </div>
            <h2 id="question-limit-modal-title" className={styles.modalTitle}>
              Question Limit Reached
            </h2>
            <p className={styles.modalMessage}>
              You&apos;ve used all {MAX_CHAT_QUESTIONS} questions in Finance Q&amp;A. For more personalized guidance,
              reach out to our support team or schedule time with your CFP — visit the Meet an Expert page to get started.
            </p>
            <div className={styles.modalDivider} aria-hidden />
            <div className={styles.modalActions}>
              <button
                className={styles.modalBackButton}
                onClick={() => {
                  capture(posthog, FINANCE_QA_EVENT, financeQaProps(embedded, { action: 'meet_an_expert' }));
                  navigate('/dashboard/meet-an-expert');
                }}
                aria-label="Meet an Expert"
              >
                Meet an Expert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalkToReesePage;